import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Form, Modal, Alert, Badge, Spinner } from 'react-bootstrap';
import api from '../../services/api';

/**
 * ApiKeys component for managing API keys
 * Allows users to create and delete API keys
 */
const ApiKeys = () => {
  // We don't need to destructure anything from useAuth()
  // Auth context is still available to our API util for auth headers
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [keyType, setKeyType] = useState('live');
  const [addingKey, setAddingKey] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentKey, setCurrentKey] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState(false);

  // Load API keys on component mount
  useEffect(() => {
    fetchApiKeys();
  }, []);

  // Fetch API keys from API
  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      const response = await api.get('/pro-features/api-keys');
      setApiKeys(response.data.data.apiKeys || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching API keys:', err);
      setError('Failed to load your API keys. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Create a new API key
  const handleCreateKey = async (e) => {
    e.preventDefault();
    setAddingKey(true);
    
    try {
      const response = await api.post('/pro-features/api-keys', { 
        name: newKeyName,
        type: keyType
      });
      
      // Get the newly created key
      const createdKey = response.data.data.apiKey;
      setNewKey(createdKey);
      
      // Add the new key to the list (without the actual key)
      setApiKeys([...apiKeys, {
        id: createdKey.id,
        name: createdKey.name,
        prefix: createdKey.prefix,
        createdAt: createdKey.createdAt,
        lastUsed: createdKey.lastUsed
      }]);
      
      setNewKeyName('');
      setKeyType('live');
    } catch (err) {
      console.error('Error creating API key:', err);
      setError(err.response?.data?.message || 'Failed to create API key. Please try again.');
    } finally {
      setAddingKey(false);
    }
  };

  // Delete an API key
  const handleDeleteKey = async () => {
    if (!currentKey) return;
    
    setDeleting(true);
    
    try {
      await api.delete(`/pro-features/api-keys/${currentKey.id}`);
      
      // Remove the key from the list
      const filteredKeys = apiKeys.filter(key => key.id !== currentKey.id);
      setApiKeys(filteredKeys);
      setShowDeleteModal(false);
    } catch (err) {
      console.error('Error deleting API key:', err);
      setError('Failed to delete API key. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // Copy key to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      (err) => {
        console.error('Could not copy text: ', err);
      }
    );
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString();
  };

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">API Keys</h5>
        <Button 
          variant="primary" 
          size="sm"
          onClick={() => setShowAddModal(true)}
        >
          Create API Key
        </Button>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        {loading ? (
          <div className="text-center p-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : apiKeys.length === 0 ? (
          <Alert variant="info">
            You haven't created any API keys yet. Create your first key to access the API.
          </Alert>
        ) : (
          <Table responsive hover>
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Created</th>
                <th>Last Used</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {apiKeys.map(key => (
                <tr key={key.id}>
                  <td>{key.name}</td>
                  <td>
                    {key.prefix?.startsWith('test_') ? (
                      <Badge bg="secondary">Test</Badge>
                    ) : (
                      <Badge bg="success">Live</Badge>
                    )}
                  </td>
                  <td>{formatDate(key.createdAt)}</td>
                  <td>{formatDate(key.lastUsed)}</td>
                  <td>
                    <Button 
                      variant="outline-danger" 
                      size="sm"
                      onClick={() => {
                        setCurrentKey(key);
                        setShowDeleteModal(true);
                      }}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        <div className="mt-3">
          <h6>Using API Keys</h6>
          <p>
            API keys allow you to integrate our affiliate tracking system with your own applications.
            Use the API to programmatically create links, track conversions, and more.
          </p>
          
          <h6>Important Security Notes:</h6>
          <ul>
            <li>Store your API keys securely and never expose them in client-side code.</li>
            <li>API keys have full access to your account, so treat them like passwords.</li>
            <li>You can create test keys for development and live keys for production.</li>
            <li>Use HTTPS when making API requests to ensure your data is encrypted.</li>
          </ul>
          
          <Alert variant="info">
            For full API documentation and examples, visit our <a href="/api-docs" target="_blank" rel="noopener noreferrer">API Documentation</a>.
          </Alert>
        </div>
      </Card.Body>

      {/* Add API Key Modal */}
      <Modal show={showAddModal} onHide={() => {
        if (!newKey) {
          setShowAddModal(false);
        }
      }}>
        <Modal.Header closeButton={!newKey}>
          <Modal.Title>{newKey ? 'API Key Created' : 'Create API Key'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {newKey ? (
            <>
              <Alert variant="success">
                Your API key has been created. This is the only time you will see this key.
                Please copy it and store it securely.
              </Alert>
              <div className="mb-3">
                <p><strong>Name:</strong> {newKey.name}</p>
                <p><strong>Type:</strong> {newKey.prefix.startsWith('test_') ? 'Test' : 'Live'}</p>
                <div className="bg-light p-3 rounded">
                  <div className="d-flex justify-content-between align-items-center">
                    <code className="user-select-all">{newKey.key}</code>
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={() => copyToClipboard(newKey.key)}
                    >
                      {copied ? 'Copied!' : 'Copy'}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <Form onSubmit={handleCreateKey}>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control 
                  type="text" 
                  placeholder="My API Key"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  required
                />
                <Form.Text className="text-muted">
                  Choose a descriptive name to identify this key.
                </Form.Text>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Key Type</Form.Label>
                <Form.Select 
                  value={keyType}
                  onChange={(e) => setKeyType(e.target.value)}
                >
                  <option value="live">Live</option>
                  <option value="test">Test</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  Test keys are for development environments. Live keys are for production use.
                </Form.Text>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          {newKey ? (
            <Button variant="primary" onClick={() => {
              setShowAddModal(false);
              setNewKey(null);
            }}>
              Done
            </Button>
          ) : (
            <>
              <Button variant="secondary" onClick={() => setShowAddModal(false)} disabled={addingKey}>
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={handleCreateKey}
                disabled={addingKey || !newKeyName}
              >
                {addingKey ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                    <span className="ms-2">Creating...</span>
                  </>
                ) : 'Create Key'}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Delete API Key Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete API Key</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete the API key <strong>{currentKey?.name}</strong>?</p>
          <Alert variant="warning">
            This action cannot be undone. Any applications using this key will no longer be able to access the API.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDeleteKey}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                <span className="ms-2">Deleting...</span>
              </>
            ) : 'Delete Key'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default ApiKeys;
